import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, Chip, Button,
  CircularProgress, Alert, Switch, FormControlLabel, Tooltip, IconButton, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadarIcon from '@mui/icons-material/Radar';
import BlockIcon from '@mui/icons-material/Block';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import AddDomainDialog from './AddDomainDialog.js';
import { EditableText, EditableSelect, FocusToggle } from './EditableField.js';
import { registerUnsavedGuard } from '../../hooks/useUnsavedGuard.js';
import type {
  DomainDetail, DomainListItem, OnboardingStatus, ScanEventDetail, EventTargetDetail,
  RerunDiscoveryResponse,
} from '../../../shared/watchdogTypes.js';

const STATUS_META: Record<OnboardingStatus, { label: string; color: string }> = {
  new: { label: 'neu', color: colors.black60 },
  discovery_running: { label: 'Discovery läuft', color: colors.scoreYellow },
  active: { label: 'aktiv', color: colors.scoreGreen },
  failed: { label: 'fehlgeschlagen', color: colors.scoreRed },
};

const SIM_METHOD_LABEL: Record<string, string> = {
  datalayer_push: 'dataLayer-Push',
  navigation: 'Navigation',
  synthetic_push: 'synthetischer Push',
  unclear: 'nicht simulierbar',
};

/* Auswahlwerte — Spiegel der Enums aus dem Datenmodell v0.1. */
const OPT_ONBOARDING = [
  { value: 'new', label: 'neu' },
  { value: 'discovery_running', label: 'Discovery läuft' },
  { value: 'active', label: 'aktiv' },
  { value: 'failed', label: 'fehlgeschlagen' },
] as const;
const OPT_DOMAIN_STATUS = [
  { value: 'active', label: 'aktiv — wird nachts geprüft' },
  { value: 'paused', label: 'pausiert — vererbt sich auf alle Events' },
  { value: 'disabled', label: 'deaktiviert (Soft-Delete)' },
] as const;
const OPT_CUSTOMER_TYPE = [
  { value: 'lead', label: 'Lead' }, { value: 'ecommerce', label: 'eCommerce' },
  { value: 'publisher', label: 'Publisher' }, { value: 'unclear', label: 'unklar' },
] as const;
const OPT_CONSENT_MODE = [
  { value: 'advanced', label: 'advanced' }, { value: 'basic', label: 'basic' },
  { value: 'none', label: 'none' }, { value: 'unclear', label: 'unklar' },
  { value: 'misconfigured', label: 'fehlkonfiguriert' },
] as const;
const OPT_EVENT_STATUS = [
  { value: 'active', label: 'aktiv' },
  { value: 'disabled', label: 'deaktiviert' },
  { value: 'vanished', label: 'verschwunden' },
] as const;
const OPT_TARGET_STATUS = OPT_EVENT_STATUS;
const OPT_SIM_METHOD = [
  { value: 'datalayer_push', label: 'dataLayer-Push' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'synthetic_push', label: 'synthetischer Push' },
  { value: 'unclear', label: 'nicht simulierbar' },
] as const;
const OPT_EVENT_TYPE = [
  { value: 'purchase', label: 'Kauf' }, { value: 'add_to_cart', label: 'In den Warenkorb' },
  { value: 'form_sent', label: 'Formular gesendet' }, { value: 'newsletter', label: 'Newsletter' },
  { value: 'unclear', label: 'nicht zugeordnet' },
] as const;
const OPT_CHANNEL = [
  { value: 'google_ads', label: 'Google Ads' }, { value: 'ga4', label: 'Google Analytics 4' },
  { value: 'meta', label: 'Meta Pixel' }, { value: 'linkedin', label: 'LinkedIn Insight' },
  { value: 'microsoft_uet', label: 'Microsoft Ads (UET)' }, { value: 'tiktok', label: 'TikTok Pixel' },
] as const;

/* ── Entwurf ─────────────────────────────────────────────── */

type FieldMap = Record<string, string | boolean>;

interface Draft {
  domain: FieldMap;
  settings: FieldMap;
  events: Record<string, FieldMap>;
  targets: Record<string, FieldMap>;
}

const EMPTY_DRAFT: Draft = { domain: {}, settings: {}, events: {}, targets: {} };

function countChanges(d: Draft): number {
  return Object.keys(d.domain).length + Object.keys(d.settings).length
    + Object.values(d.events).reduce((n, f) => n + Object.keys(f).length, 0)
    + Object.values(d.targets).reduce((n, f) => n + Object.keys(f).length, 0);
}

/** Fehler je Feld — Schlüssel wie "settings.cmp_name" oder "event.<id>.event_name". */
type ErrorMap = Record<string, string>;

function StatusChip({ status }: { status: OnboardingStatus | null }) {
  if (!status) return <Typography variant="caption" sx={{ color: colors.black60 }}>keine Settings</Typography>;
  const m = STATUS_META[status];
  return (
    <Chip size="small" label={m.label}
      sx={{ bgcolor: m.color + '22', color: m.color, border: `1px solid ${m.color}66`, fontWeight: 600 }} />
  );
}

export default function WatchdogDomainsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [domains, setDomains] = useState<DomainListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DomainDetail | null>(null);
  /** Aus dem Graph angesprungenes Event — wird kurz hervorgehoben. */
  const [highlightEvent, setHighlightEvent] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expert, setExpert] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<ErrorMap>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ text: string; ok: boolean } | null>(null);

  /** Offener Verlassen-Dialog samt Fortsetzung, sobald der Nutzer entschieden hat. */
  const [leavePrompt, setLeavePrompt] = useState<{ resolve: (go: boolean) => void } | null>(null);

  const changeCount = countChanges(draft);
  const isDirty = changeCount > 0;

  // Für Callbacks, die nicht bei jeder Entwurfsänderung neu gebaut werden sollen.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<DomainListItem[]>('/watchdog/domains');
    setLoading(false);
    if (!res.success) { setError(res.error.message); return; }
    setError(null);
    setDomains(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    const res = await api.get<DomainDetail>(`/watchdog/domains/${id}`);
    if (res.success) {
      setSelected(res.data);
      setDraft(EMPTY_DRAFT);
      setFieldErrors({});
    } else {
      setError(res.error.message);
    }
  }, []);

  /* ── Speichern ─────────────────────────────────────────── */

  const save = useCallback(async (): Promise<boolean> => {
    const d = draftRef.current;
    const sel = selectedRef.current;
    if (!sel || countChanges(d) === 0) return true;

    setSaving(true);
    const errors: ErrorMap = {};
    let written = 0;

    const patch = async (path: string, body: FieldMap, prefix: string) => {
      if (Object.keys(body).length === 0) return;
      const res = await api.patch(path, body);
      if (res.success) {
        written += Object.keys(body).length;
      } else {
        // Der Backend-Fehlercode trägt das Feld: INVALID_HOSTNAME -> hostname
        const field = res.error.code.replace(/^INVALID_/, '').toLowerCase();
        const key = Object.keys(body).includes(field) ? `${prefix}.${field}` : `${prefix}.*`;
        errors[key] = res.error.message;
      }
    };

    await patch(`/watchdog/domains/${sel.id}`, d.domain, 'domain');
    await patch(`/watchdog/domains/${sel.id}/settings`, d.settings, 'settings');
    for (const [id, fields] of Object.entries(d.events)) {
      await patch(`/watchdog/events/${id}`, fields, `event.${id}`);
    }
    for (const [id, fields] of Object.entries(d.targets)) {
      await patch(`/watchdog/targets/${id}`, fields, `target.${id}`);
    }

    setFieldErrors(errors);
    setSaving(false);

    const failed = Object.keys(errors).length;
    if (failed === 0) {
      await load();
      await loadDetail(sel.id);
      setToast({ text: `${written} Änderung${written === 1 ? '' : 'en'} gespeichert.`, ok: true });
      return true;
    }
    setToast({ text: `${failed} Änderung${failed === 1 ? '' : 'en'} nicht gespeichert — siehe rote Hinweise am Feld.`, ok: false });
    return false;
  }, [load, loadDetail]);

  /* ── Verlassen-Schutz ──────────────────────────────────── */

  /** Öffnet den Dialog und wartet auf die Entscheidung. */
  const askBeforeLeaving = useCallback((): Promise<boolean> => {
    if (countChanges(draftRef.current) === 0) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => setLeavePrompt({ resolve }));
  }, []);

  // Navigation im Layout fragt hier nach, solange es ungespeicherte Änderungen gibt.
  useEffect(() => {
    if (!isDirty) return;
    return registerUnsavedGuard(askBeforeLeaving);
  }, [isDirty, askBeforeLeaving]);

  // Neuladen/Schließen des Tabs — hier kann nur der Browser-Dialog greifen.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  async function guarded(action: () => void | Promise<void>) {
    if (await askBeforeLeaving()) await action();
  }

  async function openDetail(id: string) {
    if (selected?.id === id) { await guarded(() => setSelected(null)); return; }
    await guarded(async () => {
      setDetailLoading(true);
      await loadDetail(id);
      setDetailLoading(false);
    });
  }

  // Sprung aus der Graph-Ansicht: ?open=<domain_id> öffnet das Detail direkt,
  // optionales ?event=<event_id> hebt zusätzlich die Event-Zeile hervor (FR-4:
  // ein Klick auf einen Kanal soll nicht nur „irgendwo in der Domain" landen).
  useEffect(() => {
    const open = searchParams.get('open');
    if (!open) return;
    const event = searchParams.get('event');
    setDetailLoading(true);
    loadDetail(open).finally(() => setDetailLoading(false));
    setExpert(true);
    setHighlightEvent(event);
    searchParams.delete('open');
    searchParams.delete('event');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, loadDetail]);

  // Die Hervorhebung ist ein Wegweiser, kein Dauerzustand — nach ein paar
  // Sekunden verschwindet sie wieder.
  useEffect(() => {
    if (!highlightEvent) return;
    const t = setTimeout(() => setHighlightEvent(null), 6000);
    return () => clearTimeout(t);
  }, [highlightEvent]);

  /* ── Entwurf pflegen ───────────────────────────────────── */

  const setDomainField = (field: string, value: string) =>
    setDraft((d) => applyField(d, 'domain', undefined, field, value, selected?.[field as keyof DomainDetail] as string | null));
  const setSettingsField = (field: string, value: string) =>
    setDraft((d) => applyField(d, 'settings', undefined, field, value,
      (selected?.settings as unknown as Record<string, string | null>)?.[field] ?? null));
  const setEventField = (eventId: string, field: string, value: string | boolean, original: string | boolean | null) =>
    setDraft((d) => applyField(d, 'events', eventId, field, value, original));
  const setTargetField = (targetId: string, field: string, value: string, original: string | null) =>
    setDraft((d) => applyField(d, 'targets', targetId, field, value, original));

  /** Discovery Run für die geöffnete Domain erneut anstoßen. */
  const [rerunning, setRerunning] = useState(false);
  async function rerunDiscovery() {
    if (!selected) return;
    if (!(await askBeforeLeaving())) return;
    setRerunning(true);
    const res = await api.post<RerunDiscoveryResponse>(`/watchdog/domains/${selected.id}/discovery`);
    setRerunning(false);
    if (!res.success) { setToast({ text: res.error.message, ok: false }); return; }
    setToast({
      text: res.data.discovery_mode === 'direct'
        ? 'Discovery Run gestartet — dauert etwa 90–120 Sekunden. Danach neu laden.'
        : 'Discovery Run eingeplant.',
      ok: true,
    });
    await load();
    await loadDetail(selected.id);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>Domains</Typography>
          <Typography variant="body2" sx={{ color: colors.black60 }}>
            Angelegte Kunden-Domains mit ihrem Scanprofil. Klick auf eine Zeile zeigt die Details.
            {expert && ' Im Expertenmodus sind die Felder bearbeitbar — Änderungen gelten erst nach dem Speichern.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                size="small" checked={expert}
                onChange={(e) => {
                  const next = e.target.checked;
                  if (!next) { void guarded(() => setExpert(false)); return; }
                  setExpert(true);
                }}
              />
            }
            label={<Typography variant="body2">Expertenmodus</Typography>}
          />
          <Tooltip title="Graph-Ansicht">
            <IconButton size="small" onClick={() => guarded(() => navigate('/watchdog/graph'))}>
              <AccountTreeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Neu laden">
            <IconButton size="small" onClick={() => guarded(load)}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Button
            variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' } }}
          >
            Domain hinzufügen
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, mb: 3 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : domains.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: colors.black60 }}>
            <Typography variant="body2">Noch keine Domain angelegt.</Typography>
            <Typography variant="caption">
              Über „Domain hinzufügen" oder direkt aus dem GTM Analyst nach einer Analyse.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: colors.lightGrey }}>
                <TableCell sx={{ width: '26%' }}>Domain</TableCell>
                <TableCell sx={{ width: '15%' }}>Workspace</TableCell>
                <TableCell sx={{ width: '13%' }}>Status</TableCell>
                <TableCell sx={{ width: '18%' }}>Container</TableCell>
                <TableCell sx={{ width: '14%' }}>CMP</TableCell>
                <TableCell sx={{ width: '14%' }}>Scanprofil</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domains.map((d) => (
                <TableRow
                  key={d.id} hover onClick={() => openDetail(d.id)}
                  sx={{ cursor: 'pointer', bgcolor: selected?.id === d.id ? '#f0f2f5' : undefined }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.display_name || d.hostname}</Typography>
                    {d.display_name && d.display_name !== d.hostname && (
                      <Typography variant="caption" sx={{ color: colors.black60, fontFamily: "'JetBrains Mono', monospace" }}>{d.hostname}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {d.workspace_ref
                      ? (
                        <Tooltip title={d.workspace_name ? `Workspace-Id ${d.workspace_ref}` : 'Kein Name im MDM-Replikat — das ist die Referenz selbst'}>
                          <Chip size="small" variant="outlined" label={d.workspace_name ?? d.workspace_ref} />
                        </Tooltip>
                      )
                      : <Typography variant="caption" sx={{ color: colors.black60 }}>—</Typography>}
                  </TableCell>
                  <TableCell><StatusChip status={d.onboarding_status} /></TableCell>
                  <TableCell>
                    {d.gtm_container_id
                      ? <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>
                          {d.gtm_container_id}{d.container_version ? ` · v${d.container_version}` : ''}
                        </Typography>
                      : <Typography variant="caption" sx={{ color: colors.black60 }}>nicht ausgelesen</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{d.cmp_name || <span style={{ color: colors.black60 }}>—</span>}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{d.event_count} Events</Typography>
                    <Typography variant="caption" sx={{ color: colors.black60 }}>{d.target_count} Targets</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {detailLoading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>}
      {selected && !detailLoading && (
        <DomainDetailView
          domain={selected}
          expert={expert}
          draft={draft}
          errors={fieldErrors}
          changeCount={changeCount}
          saving={saving}
          onSave={save}
          onDiscard={() => { setDraft(EMPTY_DRAFT); setFieldErrors({}); }}
          onRerunDiscovery={rerunDiscovery}
          rerunning={rerunning}
          setDomainField={setDomainField}
          setSettingsField={setSettingsField}
          setEventField={setEventField}
          setTargetField={setTargetField}
          highlightEvent={highlightEvent}
        />
      )}

      <AddDomainDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => load()}
      />

      {/* Rückmeldung nach dem Speichern */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.ok ? 4000 : 8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.ok ? 'success' : 'warning'}
          variant="filled"
          onClose={() => setToast(null)}
          icon={toast?.ok ? <CheckCircleIcon fontSize="inherit" /> : undefined}
          sx={{ fontSize: 14 }}
        >
          {toast?.text}
        </Alert>
      </Snackbar>

      {/* Warnung beim Verlassen mit ungespeicherten Änderungen */}
      <Dialog open={Boolean(leavePrompt)} onClose={() => { leavePrompt?.resolve(false); setLeavePrompt(null); }}>
        <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Es {changeCount === 1 ? 'gibt eine Änderung' : `gibt ${changeCount} Änderungen`}, die noch nicht in der
            Datenbank {changeCount === 1 ? 'steht' : 'stehen'}. Wie soll es weitergehen?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => { leavePrompt?.resolve(false); setLeavePrompt(null); }} sx={{ color: colors.black85 }}>
            Hier bleiben
          </Button>
          <Button
            onClick={() => { leavePrompt?.resolve(true); setLeavePrompt(null); setDraft(EMPTY_DRAFT); setFieldErrors({}); }}
            sx={{ color: colors.scoreRed }}
          >
            Verwerfen und fortfahren
          </Button>
          <Button
            variant="contained" startIcon={<SaveIcon />}
            onClick={async () => {
              const prompt = leavePrompt;
              setLeavePrompt(null);
              const ok = await save();
              prompt?.resolve(ok);
            }}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' } }}
          >
            Speichern und fortfahren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Setzt ein Feld im Entwurf — entspricht der Wert wieder dem Original, fällt es raus. */
function applyField(
  d: Draft,
  bucket: keyof Draft,
  id: string | undefined,
  field: string,
  value: string | boolean,
  original: string | boolean | null,
): Draft {
  const same = typeof value === 'boolean'
    ? value === Boolean(original)
    : String(value ?? '').trim() === String(original ?? '').trim();

  if (bucket === 'domain' || bucket === 'settings') {
    const next = { ...(d[bucket] as FieldMap) };
    if (same) delete next[field]; else next[field] = value;
    return { ...d, [bucket]: next };
  }

  const group = { ...(d[bucket] as Record<string, FieldMap>) };
  const fields = { ...(group[id!] ?? {}) };
  if (same) delete fields[field]; else fields[field] = value;
  if (Object.keys(fields).length === 0) delete group[id!]; else group[id!] = fields;
  return { ...d, [bucket]: group };
}

/* ── Detail ──────────────────────────────────────────────── */

interface DetailProps {
  domain: DomainDetail;
  expert: boolean;
  draft: Draft;
  errors: ErrorMap;
  changeCount: number;
  saving: boolean;
  onSave: () => Promise<boolean>;
  onDiscard: () => void;
  onRerunDiscovery: () => Promise<void>;
  rerunning: boolean;
  setDomainField: (field: string, value: string) => void;
  setSettingsField: (field: string, value: string) => void;
  setEventField: (eventId: string, field: string, value: string | boolean, original: string | boolean | null) => void;
  setTargetField: (targetId: string, field: string, value: string, original: string | null) => void;
  /** Aus dem Graph angesprungenes Event (FR-4) — null, wenn regulaer geoeffnet. */
  highlightEvent?: string | null;
}

function DomainDetailView(p: DetailProps) {
  const { domain, expert, draft, errors, changeCount, saving, onSave, onDiscard, onRerunDiscovery, rerunning } = p;
  const s = domain.settings;
  const events = domain.scan_events ?? [];
  const visibleEvents = expert ? events : events.filter((e) => e.scan_event_status !== 'vanished');
  const ro = !expert;

  /** Wert aus dem Entwurf, sonst aus der Datenbank. */
  const dv = (bucket: 'domain' | 'settings', field: string, original: string | null): string | null => {
    const v = (draft[bucket] as FieldMap)[field];
    return v === undefined ? original : String(v);
  };

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h6">
          {domain.display_name || domain.hostname}
          <Typography component="span" variant="body2" sx={{ color: colors.black60, ml: 1, fontFamily: "'JetBrains Mono', monospace" }}>
            {domain.hostname}
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {expert && (
            <Tooltip title={
              s?.onboarding_status === 'discovery_running'
                ? 'Es läuft bereits ein Discovery Run'
                : 'Seite erneut scannen und das Scanprofil aktualisieren'
            }>
              <span>
                <Button
                  size="small" variant="outlined"
                  startIcon={rerunning ? <CircularProgress size={14} /> : <RadarIcon />}
                  onClick={() => void onRerunDiscovery()}
                  disabled={rerunning || s?.onboarding_status === 'discovery_running'}
                  sx={{ borderColor: colors.silver, color: colors.black85 }}
                >
                  Discovery Run starten
                </Button>
              </span>
            </Tooltip>
          )}
          <StatusChip status={s?.onboarding_status ?? null} />
        </Box>
      </Box>

      {expert && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.2,
          borderRadius: 1,
          bgcolor: changeCount > 0 ? '#fff8e1' : colors.lightGrey,
          border: `1px solid ${changeCount > 0 ? colors.scoreYellow : colors.silver}`,
        }}>
          <Typography variant="body2" sx={{ flex: 1, color: colors.black85 }}>
            {changeCount > 0
              ? <><b>{changeCount}</b> ungespeicherte Änderung{changeCount === 1 ? '' : 'en'}</>
              : 'Keine offenen Änderungen'}
          </Typography>
          <Button
            size="small" startIcon={<UndoIcon />} onClick={onDiscard} disabled={changeCount === 0 || saving}
            sx={{ color: colors.black85 }}
          >
            Verwerfen
          </Button>
          <Button
            size="small" variant="contained"
            startIcon={saving ? <CircularProgress size={14} sx={{ color: colors.black }} /> : <SaveIcon />}
            onClick={() => void onSave()}
            disabled={changeCount === 0 || saving}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' }, '&.Mui-disabled': { bgcolor: colors.lightGrey } }}
          >
            Speichern
          </Button>
        </Box>
      )}

      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          {expert && (
            <>
              <DetailRow label="Hostname">
                <EditableText
                  value={dv('domain', 'hostname', domain.hostname)} original={domain.hostname}
                  readOnly={ro} mono error={errors['domain.hostname'] ?? errors['domain.*']}
                  onChange={(v) => p.setDomainField('hostname', v)}
                />
              </DetailRow>
              <DetailRow label="Anzeigename">
                <EditableText
                  value={dv('domain', 'display_name', domain.display_name)} original={domain.display_name}
                  readOnly={ro} placeholder={domain.hostname} error={errors['domain.display_name']}
                  onChange={(v) => p.setDomainField('display_name', v)}
                />
              </DetailRow>
            </>
          )}
          <DetailRow label="Container">
            {expert
              ? <Box sx={{ display: 'flex', gap: 1 }}>
                  <EditableText
                    value={dv('settings', 'gtm_container_id', s?.gtm_container_id ?? null)} original={s?.gtm_container_id ?? null}
                    readOnly={ro} mono placeholder="GTM-XXXXXXX" error={errors['settings.gtm_container_id']}
                    onChange={(v) => p.setSettingsField('gtm_container_id', v)}
                  />
                  <EditableText
                    value={dv('settings', 'container_version', s?.container_version ?? null)} original={s?.container_version ?? null}
                    readOnly={ro} mono placeholder="Version" error={errors['settings.container_version']}
                    onChange={(v) => p.setSettingsField('container_version', v)}
                  />
                </Box>
              : s?.gtm_container_id
                ? <>{s.gtm_container_id}{s.container_version ? ` · Version ${s.container_version}` : ''}</>
                : <span style={{ color: colors.black60 }}>nicht ausgelesen</span>}
          </DetailRow>
          <DetailRow label="CMP">
            <EditableText
              value={dv('settings', 'cmp_name', s?.cmp_name ?? null)} original={s?.cmp_name ?? null}
              readOnly={ro} error={errors['settings.cmp_name']}
              onChange={(v) => p.setSettingsField('cmp_name', v)}
            />
          </DetailRow>
          <DetailRow label="Consent Mode">
            <EditableSelect
              value={dv('settings', 'consent_mode', s?.consent_mode ?? 'unclear')} original={s?.consent_mode ?? 'unclear'}
              readOnly={ro} options={OPT_CONSENT_MODE} error={errors['settings.consent_mode']}
              onChange={(v) => p.setSettingsField('consent_mode', v)}
            />
          </DetailRow>
          <DetailRow label="Kundentyp">
            <EditableSelect
              value={dv('settings', 'customer_type', s?.customer_type ?? 'unclear')} original={s?.customer_type ?? 'unclear'}
              readOnly={ro} options={OPT_CUSTOMER_TYPE} error={errors['settings.customer_type']}
              onChange={(v) => p.setSettingsField('customer_type', v)}
            />
          </DetailRow>
          <DetailRow label="Workspace-Referenz">
            <EditableText
              value={dv('domain', 'workspace_ref', domain.workspace_ref)} original={domain.workspace_ref}
              readOnly={ro} error={errors['domain.workspace_ref']}
              onChange={(v) => p.setDomainField('workspace_ref', v)}
            />
          </DetailRow>
          {expert && (
            <>
              <DetailRow label="Onboarding-Status">
                <EditableSelect
                  value={dv('settings', 'onboarding_status', s?.onboarding_status ?? 'new')} original={s?.onboarding_status ?? 'new'}
                  readOnly={ro} options={OPT_ONBOARDING} error={errors['settings.onboarding_status']}
                  onChange={(v) => p.setSettingsField('onboarding_status', v)}
                />
              </DetailRow>
              <DetailRow label="Betriebszustand">
                <EditableSelect
                  value={dv('settings', 'domain_status', s?.domain_status ?? 'active')} original={s?.domain_status ?? 'active'}
                  readOnly={ro} options={OPT_DOMAIN_STATUS} error={errors['settings.domain_status']}
                  onChange={(v) => p.setSettingsField('domain_status', v)}
                />
              </DetailRow>
              <DetailRow label="Domain-ID">
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: colors.black60 }}>{domain.id}</span>
              </DetailRow>
              <DetailRow label="Angelegt">{new Date(domain.created_at).toLocaleString('de-DE')}</DetailRow>
            </>
          )}
        </TableBody>
      </Table>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Events <Typography component="span" variant="caption" sx={{ color: colors.black60 }}>
          ({visibleEvents.length}{expert ? '' : ' aktiv'})
        </Typography>
      </Typography>

      {visibleEvents.length === 0 ? (
        <Typography variant="body2" sx={{ color: colors.black60 }}>
          Kein Scanprofil vorhanden — die Domain wurde auf Ebene 1 angelegt oder der Container enthielt keine zuordenbaren Conversion-Tags.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell sx={{ width: '28%' }}>Event</TableCell>
              <TableCell sx={{ width: '14%' }}>Typ</TableCell>
              <TableCell sx={{ width: expert ? '28%' : '43%' }}>Kanäle</TableCell>
              <TableCell sx={{ width: '15%' }}>Simulation</TableCell>
              {expert && <TableCell sx={{ width: '13%' }}>Requests</TableCell>}
              {expert && <TableCell sx={{ width: '13%' }}>Status</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleEvents.map((e) => (
              <EventRow
                key={e.id} event={e} expert={expert}
                draft={draft.events[e.id] ?? {}} errors={errors}
                targetDrafts={draft.targets}
                setEventField={p.setEventField} setTargetField={p.setTargetField}
                highlighted={p.highlightEvent === e.id}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell sx={{ width: 200, color: colors.black60, border: 0, py: 0.5, verticalAlign: 'top' }}>{label}</TableCell>
      <TableCell sx={{ border: 0, py: 0.5 }}>{children}</TableCell>
    </TableRow>
  );
}

function EventRow({ event, expert, draft, errors, targetDrafts, setEventField, setTargetField, highlighted }: {
  event: ScanEventDetail;
  expert: boolean;
  draft: FieldMap;
  errors: ErrorMap;
  targetDrafts: Record<string, FieldMap>;
  setEventField: DetailProps['setEventField'];
  setTargetField: DetailProps['setTargetField'];
  /** Aus dem Graph angesprungen: Zeile einfaerben und in den Blick holen. */
  highlighted?: boolean;
}) {
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (highlighted) rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlighted]);

  const targets = expert ? event.targets : event.targets.filter((t) => t.event_target_status === 'active');
  const isUnclear = event.event_type_fk === 'unclear';
  const ro = !expert;
  const err = (field: string) => errors[`event.${event.id}.${field}`] ?? errors[`event.${event.id}.*`];
  const val = (field: string, original: string): string => {
    const v = draft[field];
    return v === undefined ? original : String(v);
  };
  const focusValue = draft.focus_event === undefined ? event.focus_event : Boolean(draft.focus_event);
  const blockValue = draft.block_requests === undefined ? event.block_requests !== false : Boolean(draft.block_requests);

  return (
    <TableRow
      ref={rowRef}
      sx={highlighted ? { bgcolor: '#fff8e1', transition: 'background-color .4s' } : { transition: 'background-color .4s' }}
    >
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
          <Box sx={{ pt: expert ? 1.3 : 0 }}>
            <FocusToggle
              active={focusValue} original={event.focus_event} readOnly={ro}
              onChange={(next) => setEventField(event.id, 'focus_event', next, event.focus_event)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <EditableText
              value={val('event_name', event.event_name)} original={event.event_name}
              readOnly={ro} mono error={err('event_name')}
              onChange={(v) => setEventField(event.id, 'event_name', v, event.event_name)}
            />
            {ro && event.focus_event && event.focus_set_by && (
              <Typography variant="caption" sx={{ color: colors.black60 }}>Fokus · {event.focus_set_by}</Typography>
            )}
          </Box>
        </Box>
        {expert && (
          <Box sx={{ mt: 0.6 }}>
            <Typography variant="caption" sx={{ color: colors.black60 }}>Test-URL</Typography>
            <EditableText
              value={val('test_url', event.test_url ?? '')} original={event.test_url}
              readOnly={ro} placeholder="/pfad — leer = Startseite" error={err('test_url')}
              onChange={(v) => setEventField(event.id, 'test_url', v, event.test_url)}
            />
          </Box>
        )}
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        {expert
          ? <EditableSelect
              value={val('event_type_fk', event.event_type_fk)} original={event.event_type_fk}
              readOnly={ro} options={OPT_EVENT_TYPE} error={err('event_type_fk')}
              onChange={(v) => setEventField(event.id, 'event_type_fk', v, event.event_type_fk)}
            />
          : <Chip
              size="small" label={event.event_type?.display_name ?? event.event_type_fk}
              variant={isUnclear ? 'outlined' : 'filled'}
              sx={isUnclear
                ? { borderColor: colors.silver, color: colors.black60 }
                : { bgcolor: colors.lime + '33', color: colors.black85, fontWeight: 600 }}
            />}
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        {targets.length === 0
          ? <Typography variant="caption" sx={{ color: colors.black60 }}>keine</Typography>
          : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: expert ? 1.2 : 0.4 }}>
              {targets.map((t) => (
                <TargetRow
                  key={t.id} target={t} expert={expert}
                  draft={targetDrafts[t.id] ?? {}} errors={errors} setTargetField={setTargetField}
                />
              ))}
            </Box>
          )}
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        {expert
          ? <EditableSelect
              value={val('sim_method', event.sim_method)} original={event.sim_method}
              readOnly={ro} options={OPT_SIM_METHOD} error={err('sim_method')}
              onChange={(v) => setEventField(event.id, 'sim_method', v, event.sim_method)}
            />
          : <Typography variant="caption" sx={{ color: event.sim_method === 'unclear' ? colors.scoreYellow : colors.black85 }}>
              {SIM_METHOD_LABEL[event.sim_method] ?? event.sim_method}
            </Typography>}
      </TableCell>

      {expert && (
        <TableCell sx={{ verticalAlign: 'top' }}>
          <BlockRequestsSwitch
            value={blockValue} original={event.block_requests}
            onChange={(next) => setEventField(event.id, 'block_requests', next, event.block_requests)}
          />
        </TableCell>
      )}

      {expert && (
        <TableCell sx={{ verticalAlign: 'top' }}>
          <EditableSelect
            value={val('scan_event_status', event.scan_event_status)} original={event.scan_event_status}
            readOnly={ro} options={OPT_EVENT_STATUS} error={err('scan_event_status')}
            onChange={(v) => setEventField(event.id, 'scan_event_status', v, event.scan_event_status)}
          />
          {event.trigger_conditions && event.trigger_conditions.length > 0 && (
            <Tooltip title={<Box component="ul" sx={{ pl: 2, m: 0 }}>{event.trigger_conditions.map((c, i) => <li key={i}>{c}</li>)}</Box>}>
              <Typography variant="caption" sx={{ color: colors.scoreYellow, cursor: 'help', display: 'block', mt: 0.5 }}>
                {event.trigger_conditions.length} Bedingung(en)
              </Typography>
            </Tooltip>
          )}
          {event.event_payload && (
            <Tooltip title={<pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(event.event_payload, null, 2)}</pre>}>
              <Typography variant="caption" sx={{ color: colors.black60, cursor: 'help', display: 'block' }}>Testdaten hinterlegt</Typography>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

/**
 * Requests des Events blocken oder durchlassen.
 *
 * Standard ist blocken: Playwright beobachtet die Requests und stoppt sie, damit
 * keine Testdaten in Google Ads, Meta oder GA4 landen. Wird der Schalter
 * ausgeschaltet, laufen die Requests durch — der Wert geht beim Discovery Run an
 * n8n und von dort an Playwright.
 */
function BlockRequestsSwitch({ value, original, onChange }: {
  value: boolean;
  original: boolean;
  onChange: (next: boolean) => void;
}) {
  const dirty = value !== original;
  return (
    <Box>
      <Tooltip title={value
        ? 'Requests werden beobachtet und geblockt — es kommen keine Testdaten in echten Systemen an.'
        : 'ACHTUNG: Requests laufen durch und erreichen die echten Zielsysteme.'}
      >
        <FormControlLabel
          sx={{ ml: 0, mr: 0 }}
          control={
            <Switch
              size="small" checked={value}
              onChange={(e) => onChange(e.target.checked)}
              sx={value ? undefined : { '& .MuiSwitch-thumb': { bgcolor: colors.scoreRed } }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              {value
                ? <BlockIcon sx={{ fontSize: 14, color: colors.black60 }} />
                : <BlockIcon sx={{ fontSize: 14, color: colors.scoreRed, transform: 'rotate(45deg)' }} />}
              <Typography variant="caption" sx={{ color: value ? colors.black60 : colors.scoreRed, fontWeight: value ? 400 : 600 }}>
                {value ? 'blocken' : 'durchlassen'}
              </Typography>
            </Box>
          }
        />
      </Tooltip>
      {dirty && (
        <Typography variant="caption" sx={{ color: colors.scoreYellow, display: 'block' }}>geändert</Typography>
      )}
    </Box>
  );
}

function TargetRow({ target, expert, draft, errors, setTargetField }: {
  target: EventTargetDetail;
  expert: boolean;
  draft: FieldMap;
  errors: ErrorMap;
  setTargetField: DetailProps['setTargetField'];
}) {
  const err = (field: string) => errors[`target.${target.id}.${field}`] ?? errors[`target.${target.id}.*`];
  const val = (field: string, original: string): string => {
    const v = draft[field];
    return v === undefined ? original : String(v);
  };
  // R6 P2: die Seite, auf der das Event fuer dieses Ziel gepusht wird (channel_info.test_url) —
  // abgeleitet aus der Bedingung oder von Hand gesetzt; leer = die Seite des Events.
  const info = target.channel_info ?? {};
  const simUrl = typeof info.test_url === 'string' && info.test_url ? info.test_url : null;
  const simSource = info.test_url_source === 'mso' ? 'von Hand' : info.test_url_source === 'derived' ? 'abgeleitet aus der Bedingung' : null;

  if (!expert) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
        <Typography variant="body2">{target.channel?.display_name ?? target.channel_fk}</Typography>
        {target.event_target_status !== 'active' && (
          <Chip size="small" label={target.event_target_status}
            sx={{ height: 18, fontSize: 10, bgcolor: colors.lightGrey, color: colors.black60 }} />
        )}
        {simUrl && (
          <Typography variant="caption" sx={{ color: colors.black60 }}>Simulation auf {simUrl}</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ border: `1px solid ${colors.lightGrey}`, borderRadius: 1, p: 0.8 }}>
      <EditableSelect
        value={val('channel_fk', target.channel_fk)} original={target.channel_fk}
        options={OPT_CHANNEL} error={err('channel_fk')}
        onChange={(v) => setTargetField(target.id, 'channel_fk', v, target.channel_fk)}
      />
      <Box sx={{ display: 'flex', gap: 0.6, mt: 0.6 }}>
        <EditableText
          value={val('channel_id', target.channel_id ?? '')} original={target.channel_id}
          placeholder="Kennung" mono error={err('channel_id')}
          onChange={(v) => setTargetField(target.id, 'channel_id', v, target.channel_id)}
        />
        <EditableText
          value={val('conversion_label', target.conversion_label ?? '')} original={target.conversion_label}
          placeholder="Conversion-Label" mono error={err('conversion_label')}
          onChange={(v) => setTargetField(target.id, 'conversion_label', v, target.conversion_label)}
        />
      </Box>
      <Box sx={{ mt: 0.6 }}>
        <EditableSelect
          value={val('event_target_status', target.event_target_status)} original={target.event_target_status}
          options={OPT_TARGET_STATUS} error={err('event_target_status')}
          onChange={(v) => setTargetField(target.id, 'event_target_status', v, target.event_target_status)}
        />
      </Box>
      <Box sx={{ mt: 0.6 }}>
        <Typography variant="caption" sx={{ color: colors.black60 }}>
          Test-URL des Ziels{simSource && draft.test_url === undefined ? ` · ${simSource}` : ''}
        </Typography>
        <EditableText
          value={val('test_url', simUrl ?? '')} original={simUrl}
          placeholder="/pfad oder https://… — leer = Seite des Events" mono error={err('test_url')}
          onChange={(v) => setTargetField(target.id, 'test_url', v, simUrl)}
        />
      </Box>
    </Box>
  );
}
